import * as React from 'react'

import { action, computed, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'

import { ONE_MINUTE_HEIGHT_PX } from '~/client/src/shared/stores/ui/CalendarView.store'
import ProjectDateStore, {
  MINUTES_IN_HOUR,
} from '~/client/src/shared/stores/ui/ProjectDate.store'

import { NOOP } from '../../../utils/noop'

interface IProps {
  activeDate?: Date
  hour: number
  timeInterval: number
  rowsNumber: number
  onEmptyCellTouchStart?: (date: Date, event?: React.SyntheticEvent) => void
  shouldHandleMouseClick?: boolean

  projectDateStore?: ProjectDateStore
}

interface IRow {
  className: string
  onRowTouchStart?: (event: React.SyntheticEvent) => void
  onRowMouseDown?: (event: React.SyntheticEvent) => void
}

interface INonWorkTimeStyles {
  height: number
  marginTop: number
  zIndex: number
}

@inject('projectDateStore')
@observer
export default class HourSpace extends React.Component<IProps> {
  @observable private rowsNumber = null
  @observable private timeInterval = null
  @observable private shouldHandleMouseClick = null
  public constructor(props: IProps) {
    super(props)

    this.rowsNumber = props.rowsNumber
    this.timeInterval = props.timeInterval
    this.shouldHandleMouseClick = props.shouldHandleMouseClick
    makeObservable(this)
  }

  @action
  public componentDidUpdate(): void {
    if (this.rowsNumber !== this.props.rowsNumber)
      this.rowsNumber = this.props.rowsNumber
    if (this.timeInterval !== this.props.timeInterval)
      this.timeInterval = this.props.timeInterval
    if (this.shouldHandleMouseClick !== this.props.shouldHandleMouseClick)
      this.shouldHandleMouseClick = this.props.shouldHandleMouseClick
  }

  private get rowStyle() {
    return { height: ONE_MINUTE_HEIGHT_PX * this.props.timeInterval, zIndex: 2 }
  }

  @computed
  private get minutesRows(): IRow[] {
    return Array.from(Array(this.rowsNumber)).map((v, i) => {
      const onTouchHandler = this.onRowTouchStart.bind(
        this,
        i * this.timeInterval,
      )

      const row = {
        element: null,
        onRowTouchStart: !this.shouldHandleMouseClick ? onTouchHandler : NOOP,
        onRowMouseDown: this.shouldHandleMouseClick ? onTouchHandler : NOOP,
        className:
          i + 1 < this.rowsNumber ? 'bb-light-grey-dashed' : 'bb-light-grey',
      }

      return row
    })
  }

  public render() {
    return (
      <div className="col">
        {this.minutesRows.map((row, index) => {
          return (
            <div
              key={index}
              className={row.className}
              onTouchStart={row.onRowTouchStart}
              onMouseDown={row.onRowMouseDown}
              style={this.rowStyle}
            />
          )
        })}
        {this.renderNonWorkTimeBackground()}
      </div>
    )
  }

  /**
   * The stretches of this hour that are not worked, as the complement of the
   * day's worked spans (STX1-983). Span-based rather than start/end arithmetic
   * because a day can carry two of them: its own window plus the tail of an
   * overnight window from the day before.
   */
  private get nonWorkingStyles(): INonWorkTimeStyles[] {
    const { hour, activeDate, projectDateStore } = this.props

    const hourStart = hour * MINUTES_IN_HOUR
    const hourEnd = hourStart + MINUTES_IN_HOUR
    const workingSpans = projectDateStore.getWorkingMinuteSpansForDate(
      activeDate || new Date(),
    )

    const styles: INonWorkTimeStyles[] = []
    let cursor = hourStart

    workingSpans.forEach(([spanStart, spanEnd]) => {
      // Clamped into the hour at BOTH ends: a span that starts after this hour
      // must not stretch the preceding gap past hourEnd, or the block overflows
      // its container and paints over the hours below it.
      const start = this.clampToHour(spanStart, hourStart, hourEnd)
      const end = this.clampToHour(spanEnd, hourStart, hourEnd)

      if (start > cursor) {
        styles.push(this.getStyle(start - cursor, cursor - hourStart))
      }
      cursor = Math.max(cursor, end)
    })

    if (cursor < hourEnd) {
      styles.push(this.getStyle(hourEnd - cursor, cursor - hourStart))
    }

    return styles
  }

  private renderNonWorkTimeBackground(): JSX.Element {
    return (
      <>
        {this.nonWorkingStyles.map((style, index) => (
          <div
            key={index}
            className="absolute bg-palette-brand-lightest full-width"
            style={style}
          />
        ))}
      </>
    )
  }

  private onRowTouchStart(minutes: number, event: any) {
    const {
      onEmptyCellTouchStart,
      hour,
      activeDate,
      projectDateStore: { setHours },
    } = this.props

    if (onEmptyCellTouchStart) {
      const data = setHours(activeDate || null, hour, minutes)
      onEmptyCellTouchStart(data, event)
    }
  }

  private clampToHour = (
    minutes: number,
    hourStart: number,
    hourEnd: number,
  ): number => {
    return Math.min(Math.max(minutes, hourStart), hourEnd)
  }

  private getStyle = (
    height: number,
    marginTop?: number,
  ): INonWorkTimeStyles => {
    return {
      height,
      marginTop,
      zIndex: 1,
    }
  }
}
